import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { updateClinicBrandingText } from '@/lib/api/admin';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
export function ClinicBrandingTextSettings({ clinicId, clinicName, companyName, footerText, canEdit, }) {
    const { t } = useTranslation('settings');
    return (_jsxs("div", { className: "space-y-4", children: [_jsxs("div", { children: [_jsx(Label, { children: t('reporting.brandingText') }), _jsx("p", { className: "text-muted-foreground text-xs", children: t('reporting.brandingTextDesc') })] }), _jsx(ClinicBrandingCard, { clinicId: clinicId, clinicName: clinicName, companyName: companyName, footerText: footerText, canEdit: canEdit }, `${clinicId}:${companyName ?? ''}:${footerText ?? ''}`)] }));
}
function ClinicBrandingCard({ clinicId, clinicName, companyName: initialCompanyName, footerText: initialFooterText, canEdit, }) {
    const { t } = useTranslation('settings');
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const [companyName, setCompanyName] = useState(initialCompanyName ?? '');
    const [footerText, setFooterText] = useState(initialFooterText ?? '');
    const [saving, setSaving] = useState(false);
    const isDirty = (companyName.trim() || null) !== (initialCompanyName ?? null) ||
        (footerText.trim() || null) !== (initialFooterText ?? null);
    const handleSave = async () => {
        setSaving(true);
        try {
            await updateClinicBrandingText(clinicId, {
                companyName: companyName.trim() || null,
                footerText: footerText.trim() || null,
            });
            // Refresh both the all-clinics list (super-admin selector source of
            // truth) and /users/me so the card resyncs from the server.
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: ['clinics', 'all'] }),
                queryClient.invalidateQueries({ queryKey: ['currentUser'] }),
            ]);
            toast({ title: t('reporting.brandingTextSaved') });
        }
        catch (err) {
            console.error('Failed to save clinic branding text', err);
            toast({
                title: t('reporting.brandingTextSaveFailed'),
                variant: 'destructive',
            });
        }
        finally {
            setSaving(false);
        }
    };
    return (_jsxs("div", { className: "border-border bg-background/50 space-y-3 rounded-md border p-3", children: [_jsx("div", { className: "text-sm font-medium", children: clinicName }), _jsxs("div", { className: "space-y-1.5", children: [_jsx(Label, { className: "text-muted-foreground text-xs", children: t('reporting.companyName') }), _jsx("input", { type: "text", className: "border-input bg-background placeholder:text-muted-foreground/50 focus:ring-ring w-full rounded-md border px-3 py-2 text-sm focus:ring-1 focus:outline-none", placeholder: t('reporting.companyNamePlaceholder'), value: companyName, onChange: (e) => setCompanyName(e.target.value), disabled: !canEdit })] }), _jsxs("div", { className: "space-y-1.5", children: [_jsx(Label, { className: "text-muted-foreground text-xs", children: t('reporting.footerText') }), _jsx("input", { type: "text", className: "border-input bg-background placeholder:text-muted-foreground/50 focus:ring-ring w-full rounded-md border px-3 py-2 text-sm focus:ring-1 focus:outline-none", placeholder: t('reporting.footerTextPlaceholder'), value: footerText, onChange: (e) => setFooterText(e.target.value), disabled: !canEdit })] }), _jsx("div", { className: "flex justify-end", children: _jsx(Button, { size: "sm", disabled: !isDirty || saving || !canEdit, onClick: handleSave, children: saving ? t('reporting.saving') : t('reporting.save') }) })] }));
}
